import { useState } from 'react';
import Button, { ButtonStyle } from 'components/common/Button';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { insertComponent } from 'utils/i18nUtils';

const SHEET_ID = 'PwaInstallGuide';

const PRIMARY_BTN_STYLE: ButtonStyle = { color: 'btn-bg-primary', fontColor: 'btn-txt-primary', borderRadius: 'lg' };
const TEXT_BTN_STYLE: ButtonStyle = { type: 'text', fontColor: 'btn-txt-only', borderRadius: 'lg' };

const ua = navigator.userAgent;
const isIOS = /iPhone|iPad|iPod/.test(ua);
const isIOSSafari = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
const logPwaEvent = (action: string, extra?: Record<string, any>) => {
  const logger = (window as any)._logPwaEvent;
  if (typeof logger === 'function') logger(action, extra);
};

function DotsButton({ vertical = false }: { vertical?: boolean }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-base font-bold leading-none align-middle text-dialog-txt-secondary" style={{ border: '1.5px solid var(--bcs-semantic-dialog-txt-secondary)' }}>
      {vertical ? '⋮' : '···'}
    </span>
  );
}

function StepRow({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className={`flex items-start gap-2 ${BResource.isRTL() ? 'flex-row-reverse' : ''}`} dir="auto">
      <span className="shrink-0">{number}.</span>
      <span>{children}</span>
    </div>
  );
}

function StepList({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col w-full gap-2 text-dialog-txt-secondary typography-body-sm">{children}</div>;
}

function AndroidInstallSteps({ hasPrompt }: { hasPrompt: boolean }) {
  if (hasPrompt) return null;

  return (
    <StepList>
      <StepRow number={1}>{insertComponent(brsc.pwa_install_guide_tap_menu, <DotsButton vertical />)}</StepRow>
      <StepRow number={2}>{brsc.pwa_install_guide_select_add_to_home}</StepRow>
    </StepList>
  );
}

function IOSInstallSteps() {
  const shareIcon = <img className="inline h-5 align-middle" src="/imgweb/icon/square_and_arrow_up.png" alt="share" />;
  const addIcon = <img className="inline w-5 h-5 align-middle" src="/imgweb/icon/plus_square.png" alt="add" />;

  return (
    <StepList>
      {isIOSSafari ? (
        <>
          <StepRow number={1}>{insertComponent(brsc.pwa_install_guide_tap_btn_address_bar, <DotsButton />)}</StepRow>
          <StepRow number={2}>{insertComponent(brsc.pwa_install_guide_tap_share, shareIcon)}</StepRow>
          <StepRow number={3}>{insertComponent(brsc.pwa_install_guide_tap_btn, <DotsButton />)}</StepRow>
        </>
      ) : (
        <>
          <StepRow number={1}>{insertComponent(brsc.pwa_install_guide_tap_share_address_bar, shareIcon)}</StepRow>
          <StepRow number={2}>{insertComponent(brsc.pwa_install_guide_tap_btn, <DotsButton />)}</StepRow>
        </>
      )}
      <StepRow number={isIOSSafari ? 4 : 3}>{insertComponent(brsc.pwa_install_guide_select_add_icon, addIcon)}</StepRow>
    </StepList>
  );
}

// 이미 설치된 경우: 홈 화면 아이콘 또는 주소창 메뉴 "Bermuda 열기"로 실행
function AlreadyInstalledSteps() {
  return (
    <StepList>
      <StepRow number={1}>{brsc.pwa_install_guide_already_installed_step_home}</StepRow>
      <StepRow number={2}>{insertComponent(brsc.pwa_install_guide_already_installed_step_menu, <DotsButton vertical />)}</StepRow>
    </StepList>
  );
}

function InstallSteps({ isAlreadyInstalled, hasPrompt }: { isAlreadyInstalled: boolean; hasPrompt: boolean }) {
  if (isAlreadyInstalled) return <AlreadyInstalledSteps />;
  if (isIOS) return <IOSInstallSteps />;
  return <AndroidInstallSteps hasPrompt={hasPrompt} />;
}

function PwaInstallGuideContent() {
  const { closeWithAnimation, sheets } = useBottomSheet();

  // 시트 mount 시점의 _deferredInstallPrompt 스냅샷만 사용한다.
  // mount 후 늦게 도착하는 prompt에 따른 리렌더는 일부러 처리하지 않음 — UI 깜빡임 방지.
  const [installPrompt] = useState<any>(() => (window as any)._deferredInstallPrompt ?? null);

  const hasPrompt = !!installPrompt;
  const sheetData = sheets[SHEET_ID]?.data;
  // 진입점 구분: 로그인 화면 설치 버튼('login') vs 로그인 후 부재중 콜 자동 노출(기본)
  const isFromLogin = sheetData?.source === 'login';
  // 호출처(common.js의 getPwaInstalledHint)가 'yes' | 'unknown'으로 전달.
  // 'yes'가 확정된 경우에만 "이미 설치됨" UI 노출. 그 외는 모두 설치 가이드.
  // 휴리스틱(prompt 부재로 설치 여부 추정)은 false positive 시 미설치 사용자에게
  // 잘못된 안내가 나가는 손해가 더 커서 사용하지 않는다.
  const installedHint: 'yes' | 'unknown' | undefined = sheetData?.installed;
  const isAlreadyInstalled = installedHint === 'yes';

  const close = () => {
    closeWithAnimation(SHEET_ID);
  };

  const handleInstall = async () => {
    if (!installPrompt) {
      close();
      return;
    }
    // BeforeInstallPromptEvent는 single-use. 두 번째 호출, Chrome 내부 오류,
    // 외부 install flow 인터럽트 등으로 throw/reject 가능 → 시트는 어떻게든 닫힘.
    try {
      installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      logPwaEvent('pwa-install-result', { outcome: choice?.outcome || 'unknown' });
    } catch (e) {
      BLog.warn('[pwa-install] prompt failed', e);
      logPwaEvent('pwa-install-result', { outcome: 'error' });
    }
    (window as any)._deferredInstallPrompt = null;
    close();
  };

  // 문구는 상태에 따라 3분기:
  //  1) 이미 설치됨 (Android Chrome) → 홈 화면/앱 열기 안내
  //  2) 로그인 화면 설치 버튼 → 앱 설치 가치 제안
  //  3) 로그인 후 부재중 콜 자동 노출 → 기존 "전화를 놓치지 마세요"
  let title: string;
  let body: string;
  if (isAlreadyInstalled) {
    title = brsc.pwa_install_guide_already_installed_title;
    body = brsc.pwa_install_guide_already_installed_body;
  } else if (isFromLogin) {
    title = brsc.pwa_install_guide_login_title;
    body = brsc.pwa_install_guide_login_body;
  } else {
    title = brsc.pwa_install_guide_title;
    body = brsc.pwa_install_guide_body;
  }

  return (
    <div className="flex h-full w-full flex-col items-center">
      <div className="typography-title-md text-dialog-txt-default">{title}</div>
      <div className="text-body2 pb-4 pt-3 text-dialog-txt-secondary text-center break-keep">
        {body}
      </div>

      <div className="pb-4 w-full">
        <InstallSteps isAlreadyInstalled={isAlreadyInstalled} hasPrompt={hasPrompt} />
      </div>

      <div className="flex w-full flex-col gap-1">
        {!isAlreadyInstalled && !isIOS && hasPrompt ? (
          <>
            <Button
              className="w-full"
              style={{ ...PRIMARY_BTN_STYLE, text: brsc.pwa_install_guide_btn_add }}
              onClick={handleInstall}
            />
            <Button
              className="w-full"
              style={{ ...TEXT_BTN_STYLE, text: brsc.later }}
              onClick={close}
            />
          </>
        ) : (
          <Button
            className="w-full"
            style={{ ...PRIMARY_BTN_STYLE, text: brsc.confirm }}
            onClick={close}
          />
        )}
      </div>
    </div>
  );
}

export default PwaInstallGuideContent;
